import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

const ACCEPTED = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/msword': ['.doc'],
};

export default function FileUpload({ file, onFileChange }) {
  const [dragError, setDragError] = useState('');
  const [hovered, setHovered] = useState(false);

  const onDrop = useCallback(
    (accepted, rejected) => {
      setDragError('');
      if (rejected.length > 0) {
        const reason = rejected[0].errors[0]?.message || 'Invalid file.';
        setDragError(reason);
        return;
      }
      if (accepted.length > 0) {
        onFileChange(accepted[0]);
      }
    },
    [onFileChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED,
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024,
  });

  const clearFile = (e) => {
    e.stopPropagation();
    onFileChange(null);
    setDragError('');
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const pulseHover = hovered || isDragActive;

  return (
    <div>
      <div
        {...getRootProps()}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          ...styles.zone,
          ...(pulseHover ? styles.zoneHover : {}),
          ...(file ? styles.zoneSuccess : {}),
          ...(dragError ? styles.zoneError : {}),
        }}
        id="file-drop-zone"
        role="button"
        aria-label="Resume upload drop zone"
        tabIndex={0}
      >
        <input {...getInputProps()} id="resume-file-input" aria-label="Select resume file" />

        {file ? (
          <div style={styles.fileInfo}>
            <span style={styles.fileIcon}>📄</span>
            <div style={styles.fileMeta}>
              <p style={styles.fileName}>{file.name}</p>
              <p style={styles.fileSize}>{formatSize(file.size)}</p>
            </div>
            <button
              onClick={clearFile}
              style={styles.clearBtn}
              aria-label="Remove file"
              type="button"
            >
              ✕
            </button>
          </div>
        ) : (
          <div style={styles.placeholder}>
            <div style={styles.uploadIcon} aria-hidden="true">
              {isDragActive ? '⬇️' : '☁️'}
            </div>
            <p style={styles.uploadTitle}>
              {isDragActive ? 'Drop it here!' : 'Drag & drop your resume'}
            </p>
            <p style={styles.uploadSub}>Or browse — up to 5 MB</p>
            <div style={styles.typeChips}>
              <span style={styles.typeChipPdf}>PDF</span>
              <span style={styles.typeChipDoc}>DOCX</span>
            </div>
            <div style={styles.browseBtn}>Browse files</div>
          </div>
        )}
      </div>

      {dragError && (
        <p style={styles.errorText} role="alert">
          {dragError}
        </p>
      )}
    </div>
  );
}

const styles = {
  zone: {
    border: '2px dashed #d9d4cf',
    borderRadius: 16,
    padding: '36px 22px',
    cursor: 'pointer',
    transition: 'border-color 0.22s ease, background 0.22s ease, box-shadow 0.22s ease',
    background: '#fffcfa',
    textAlign: 'center',
    outline: 'none',
  },
  zoneHover: {
    borderColor: '#ff4d1c',
    background: '#fff8f6',
    boxShadow: '0 8px 28px rgba(255, 77, 28, 0.1)',
  },
  zoneSuccess: {
    borderColor: '#16a34a',
    borderStyle: 'solid',
    background: '#f7fdf9',
    boxShadow: 'none',
  },
  zoneError: {
    borderColor: '#dc2626',
    borderStyle: 'solid',
    background: '#fff5f5',
  },
  placeholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
  },
  uploadIcon: { fontSize: '2.4rem', marginBottom: 2 },
  uploadTitle: {
    fontSize: '1.06rem',
    fontWeight: 700,
    color: '#0a0a0a',
  },
  uploadSub: {
    fontSize: '0.86rem',
    color: '#6b6b6b',
    marginTop: -4,
  },
  typeChips: {
    display: 'flex',
    gap: 10,
    marginTop: 4,
    marginBottom: 2,
    justifyContent: 'center',
  },
  typeChipPdf: {
    fontSize: '11px',
    fontWeight: 800,
    letterSpacing: '0.04em',
    color: '#b91c1c',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    padding: '5px 12px',
    borderRadius: 8,
    fontFamily: 'Inter, DM Sans, system-ui, sans-serif',
  },
  typeChipDoc: {
    fontSize: '11px',
    fontWeight: 800,
    letterSpacing: '0.04em',
    color: '#1d4ed8',
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    padding: '5px 12px',
    borderRadius: 8,
    fontFamily: 'Inter, DM Sans, system-ui, sans-serif',
  },
  browseBtn: {
    marginTop: 6,
    padding: '8px 20px',
    borderRadius: 10,
    background: 'rgba(255, 77, 28, 0.1)',
    border: '1px solid rgba(255, 77, 28, 0.45)',
    color: '#ff4d1c',
    fontSize: '0.86rem',
    fontWeight: 700,
    transition: 'all 0.2s ease',
  },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    textAlign: 'left',
    width: '100%',
    justifyContent: 'flex-start',
  },
  fileIcon: { fontSize: '1.85rem', flexShrink: 0 },
  fileMeta: { flex: 1, minWidth: 0 },
  fileName: {
    fontWeight: 700,
    color: '#0a0a0a',
    fontSize: '0.94rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  fileSize: { fontSize: '0.8rem', color: '#6b6b6b', marginTop: 2 },
  clearBtn: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    borderRadius: 8,
    width: 30,
    height: 30,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    fontSize: '0.78rem',
  },
  errorText: {
    marginTop: 8,
    color: '#dc2626',
    fontSize: '0.82rem',
    fontWeight: 500,
  },
};
